/* eslint-disable no-param-reassign */
import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import type { EmployerFeedbackStatusResponse } from 'src/openapi';
import {
  fetchEmployerFeedbackStatus,
  submitEmployerFeedback,
} from '../actions/employerFeedback';

// none → never submitted, show the "earn credits" CTA.
// pending → submitted, admin hasn't rewarded yet ("credits awaiting").
// accepted → admin granted credits ("credits earned").
export type EmployerFeedbackStatus = 'none' | 'pending' | 'accepted';

interface EmployerFeedbackState {
  status: EmployerFeedbackStatus;
  creditsAwarded: number;
  feedbackId: string | null;
  loaded: boolean;
  isSubmitting: boolean;
  isDialogOpen: boolean;
}

const initialState: EmployerFeedbackState = {
  status: 'none',
  creditsAwarded: 0,
  feedbackId: null,
  loaded: false,
  isSubmitting: false,
  isDialogOpen: false,
};

const deriveStatus = (
  payload: EmployerFeedbackStatusResponse,
): EmployerFeedbackStatus => {
  if (!payload.submitted) return 'none';
  return payload.status === 'Accepted' ? 'accepted' : 'pending';
};

const applyStatus = (
  state: EmployerFeedbackState,
  payload: EmployerFeedbackStatusResponse,
) => {
  state.status = deriveStatus(payload);
  state.creditsAwarded = payload.credits_awarded ?? 0;
  state.feedbackId = payload.feedback_id ?? null;
  state.loaded = true;
};

const employerFeedbackSlice = createSlice({
  name: 'employerFeedback',
  initialState,
  reducers: {
    setFeedbackDialogOpen: (state, action: PayloadAction<boolean>) => {
      state.isDialogOpen = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchEmployerFeedbackStatus.fulfilled, (state, action) => {
        applyStatus(state, action.payload);
      })
      .addCase(submitEmployerFeedback.pending, (state) => {
        state.isSubmitting = true;
      })
      .addCase(submitEmployerFeedback.fulfilled, (state, action) => {
        state.isSubmitting = false;
        applyStatus(state, action.payload);
      })
      .addCase(submitEmployerFeedback.rejected, (state) => {
        state.isSubmitting = false;
      });
  },
});

export const { setFeedbackDialogOpen } = employerFeedbackSlice.actions;
export default employerFeedbackSlice.reducer;
